// Whether an access token has been obtained for the session. Three states, not two
// booleans: "failed" and "present" cannot both hold, and modelling them separately
// admits a pair that means nothing.
export type PortalTokenState = "pending" | "failed" | "present";

// What a portal route should show, given what the identity provider has told us
// so far.
type PortalAuthView = "loading" | "signedOut" | "ready";

interface PortalAuthState {
  // The identity provider is still deciding whether there is a session.
  isLoading: boolean;
  isAuthenticated: boolean;
  // A profile for the session. Pages below this gate read fields off it, and the
  // implicit flow the portal uses can return a token without one.
  hasUser: boolean;
  token: PortalTokenState;
  // The visitor is on the sign-in return address, where the identity provider has
  // only just created the session. Its first "no session" answer there is routinely
  // wrong — the session is still settling — so it stays provisional until the token
  // fetch delivers a verdict. Everywhere else "no session" is trusted immediately.
  completingSignIn: boolean;
}

// The order is the point. Anyone who will never receive a token has to reach the
// signed-out screen, because waiting for one they cannot get is a blank page with
// nothing to escape it. A fetch only reports failure once retrying has stopped
// helping, so "failed" belongs in that group.
export function portalAuthView({
  isLoading,
  isAuthenticated,
  hasUser,
  token,
  completingSignIn,
}: PortalAuthState): PortalAuthView {
  if (isLoading) {
    return "loading";
  }
  if (token === "failed") {
    return "signedOut";
  }
  if (!isAuthenticated) {
    // The grace holds only while the token fetch is still working. Its success
    // does not always deliver a session — a token can arrive without the profile
    // the session flag is derived from — so any settled verdict, not just
    // "failed", must be allowed to reach the signed-out screen.
    return completingSignIn && token === "pending" ? "loading" : "signedOut";
  }
  if (!hasUser) {
    return "signedOut";
  }
  if (token === "pending") {
    return "loading";
  }
  return "ready";
}
